import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  AreaChart,
  Area,
  ComposedChart,
  ReferenceLine
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  PieChart as PieChartIcon,
  BarChart3,
  Download,
  RefreshCw,
  AlertTriangle,
  Target,
  Clock,
  Shield,
  DollarSign,
  Percent,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand colors
const COLORS = {
  primary: '#1D2D2A',
  secondary: '#315C52',
  accent: '#18A37C',
  success: '#22C55E',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  muted: '#6B7280'
};

const SECTOR_COLORS = [
  '#1D2D2A', '#315C52', '#18A37C', '#18A37C', '#74C69D',
  '#95D5B2', '#B7E4C7', '#D8F3DC', '#3B82F6', '#6366F1'
];

const STATUS_COLORS = {
  'Performing': '#22C55E',
  'Due': '#F59E0B',
  'Overdue': '#EF4444',
  'Debt Management': '#6B7280',
  // Legacy — keep for backwards compat with any stale UI cache
  'Active': '#22C55E',
  'Matured': '#3B82F6',
  'In Arrears': '#F59E0B',
  'Default': '#EF4444',
  'Written Off': '#6B7280'
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value) => {
  return `${value?.toFixed(2) || 0}%`;
};

export default function ReportsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [performancePeriod, setPerformancePeriod] = useState('1Y');
  const [compositionBy, setCompositionBy] = useState('category');

  const [performanceData, setPerformanceData] = useState(null);
  const [compositionData, setCompositionData] = useState(null);
  const [returnsData, setReturnsData] = useState(null);
  const [riskData, setRiskData] = useState(null);

  const [exporting, setExporting] = useState({});

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [perfRes, compRes, retRes, riskRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/performance?period=${performancePeriod}`, { headers }),
        axios.get(`${API_URL}/api/analytics/composition?by=${compositionBy}`, { headers }),
        axios.get(`${API_URL}/api/analytics/returns`, { headers }),
        axios.get(`${API_URL}/api/analytics/risk`, { headers })
      ]);

      setPerformanceData(perfRes.data);
      setCompositionData(compRes.data);
      setReturnsData(retRes.data);
      setRiskData(riskRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [token, performancePeriod, compositionBy]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleExport = async (type) => {
    setExporting(prev => ({ ...prev, [type]: true }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let url = '';
      let filename = '';
      
      switch (type) {
        case 'performance':
          url = `${API_URL}/api/analytics/export/performance?period=${performancePeriod}`;
          filename = `performance_report_${performancePeriod}.csv`;
          break;
        case 'composition':
          url = `${API_URL}/api/analytics/export/composition?by=${compositionBy}`;
          filename = `portfolio_composition_${compositionBy}.csv`;
          break;
        case 'returns':
          url = `${API_URL}/api/analytics/export/returns`;
          filename = 'return_analysis.csv';
          break;
        case 'risk':
          url = `${API_URL}/api/analytics/export/risk`;
          filename = 'risk_summary.csv';
          break;
        default:
          return;
      }
      
      const response = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} report exported`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(prev => ({ ...prev, [type]: false }));
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.name.includes('Value') ? formatCurrency(entry.value) : formatPercent(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="reports-loading">
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-5 w-96" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-64 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212529] font-['Manrope']">
            Reports & Analytics
          </h1>
          <p className="text-[#6C757D] mt-1">
            Comprehensive insights into your portfolio performance
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchAllData}
          className="flex items-center gap-2"
          data-testid="refresh-analytics-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Section 1: Performance Over Time */}
      <Card data-testid="performance-section">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#1D2D2A]" />
              Performance Over Time
            </CardTitle>
            <CardDescription>Portfolio value and returns tracking</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={performancePeriod} onValueChange={setPerformancePeriod}>
              <SelectTrigger className="w-24" data-testid="period-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6M">6M</SelectItem>
                <SelectItem value="1Y">1Y</SelectItem>
                <SelectItem value="2Y">2Y</SelectItem>
                <SelectItem value="ALL">All</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('performance')}
              disabled={exporting.performance}
              data-testid="export-performance-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Start Value</p>
              <p className="text-xl font-bold text-[#212529]">
                {formatCurrency(performanceData?.summary?.start_value || 0)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">
                Current Value
                {performanceData?.data?.length > 0 && (
                  <span className="ml-1 text-[10px] text-gray-400" data-testid="performance-current-as-of">
                    (as of {performanceData.data[performanceData.data.length - 1].date})
                  </span>
                )}
              </p>
              <p className="text-xl font-bold text-[#212529]">
                {formatCurrency(performanceData?.summary?.end_value || 0)}
              </p>
              {(() => {
                const last = performanceData?.data?.[performanceData.data.length - 1];
                return last && Math.abs(last.deal_room || 0) > 0.005 ? (
                  <p className="text-xs text-[#6C757D] mt-1" data-testid="performance-split">
                    Loans {formatCurrency(last.holdings)} · Cash {formatCurrency(last.cash)} · Deal Room {formatCurrency(last.deal_room)}
                  </p>
                ) : null;
              })()}
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Return</p>
              <p className={`text-xl font-bold flex items-center gap-1 ${
                (performanceData?.summary?.total_return || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {(performanceData?.summary?.total_return || 0) >= 0 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                {formatPercent(performanceData?.summary?.total_return)}
              </p>
            </div>
          </div>
          
          {/* Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performanceData?.data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-ZA', { month: 'short', year: '2-digit' });
                  }}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `R${(value / 1000000).toFixed(1)}M`}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="portfolio_value" 
                  stroke={COLORS.primary}
                  strokeWidth={2}
                  dot={false}
                  name="Portfolio Value"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="monthly_return" 
                  stroke={COLORS.success}
                  strokeWidth={2}
                  dot={false}
                  name="Monthly Return %"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Portfolio Composition */}
      <Card data-testid="composition-section">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-[#1D2D2A]" />
              Portfolio Composition
            </CardTitle>
            <CardDescription>
              Loan Holdings: {formatCurrency(compositionData?.total_value || 0)}
              {Math.abs(compositionData?.deal_room_value || 0) > 0.005 && (
                <span data-testid="composition-deal-room"> · Deal Room (off loan book, at cost): {formatCurrency(compositionData.deal_room_value)}</span>
              )}
              {compositionData?.as_of && (
                <span className="ml-2 text-xs text-[#6C757D]" data-testid="composition-as-of">as of {compositionData.as_of}</span>
              )}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('composition')}
            disabled={exporting.composition}
            data-testid="export-composition-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* By Sector */}
            <div>
              <div className="flex items-center justify-between mb-3 gap-2">
                <h4 className="text-sm font-medium text-gray-700">
                  By {compositionBy === 'sub_category' ? 'Sub-Category' :
                      compositionBy.charAt(0).toUpperCase() + compositionBy.slice(1)}
                </h4>
                <Select value={compositionBy} onValueChange={setCompositionBy}>
                  <SelectTrigger className="w-36 h-8 text-xs" data-testid="composition-by-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="category">Category</SelectItem>
                    <SelectItem value="merchandise">Merchandise</SelectItem>
                    <SelectItem value="sub_category">Sub-Category</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="deal">Deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={compositionData?.by_sector || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percentage }) => `${percentage}%`}
                      labelLine={false}
                    >
                      {(compositionData?.by_sector || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={SECTOR_COLORS[index % SECTOR_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {(compositionData?.by_sector || []).slice(0, 5).map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: SECTOR_COLORS[index % SECTOR_COLORS.length] }}
                      />
                      <span className="truncate max-w-[100px]">{item.name}</span>
                    </div>
                    <span className="font-medium">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Status */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 text-center">By Status</h4>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={compositionData?.by_status || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percentage }) => `${percentage}%`}
                      labelLine={false}
                    >
                      {(compositionData?.by_status || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || STATUS_COLORS[entry.name] || COLORS.muted} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1">
                {(compositionData?.by_status || []).map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color || STATUS_COLORS[item.name] || COLORS.muted }}
                      />
                      <span>{item.name}</span>
                    </div>
                    <span className="font-medium">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Remaining Term */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3 text-center">By Remaining Term</h4>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={compositionData?.by_term || []}
                    layout="vertical"
                    margin={{ left: 10, right: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={(value) => `R${(value / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="name" width={50} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill={COLORS.secondary} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2 space-y-1">
                {(compositionData?.by_term || []).map((item, index) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <span>{item.name}</span>
                    <span className="font-medium">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Return Analysis */}
      <Card data-testid="returns-section">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#1D2D2A]" />
              Return Analysis
            </CardTitle>
            <CardDescription>IRR, vintage returns, and benchmark comparison</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('returns')}
            disabled={exporting.returns}
            data-testid="export-returns-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-[#1D2D2A]/5 rounded-lg p-4 border border-[#1D2D2A]/10">
              <div className="flex items-center gap-2 mb-1">
                <Percent className="h-4 w-4 text-[#1D2D2A]" />
                <span className="text-sm text-gray-600">Annualized Return</span>
              </div>
              <p className="text-2xl font-bold text-[#1D2D2A]">
                {formatPercent(returnsData?.summary?.annualized_return)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Benchmark (Prime)</span>
              </div>
              <p className="text-2xl font-bold text-gray-700">
                {formatPercent(returnsData?.summary?.benchmark_rate)}
              </p>
            </div>
            <div className={`rounded-lg p-4 ${
              (returnsData?.summary?.alpha || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className={`h-4 w-4 ${
                  (returnsData?.summary?.alpha || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`} />
                <span className="text-sm text-gray-600">Alpha vs Benchmark</span>
              </div>
              <p className={`text-2xl font-bold ${
                (returnsData?.summary?.alpha || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {(returnsData?.summary?.alpha || 0) >= 0 ? '+' : ''}{formatPercent(returnsData?.summary?.alpha)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Total Interest Received</span>
              </div>
              <p className="text-2xl font-bold text-gray-700">
                {formatCurrency(returnsData?.summary?.total_interest_received)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Returns by Month Matrix */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Returns by Month</h4>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs font-semibold sticky left-0 bg-gray-50">Year</TableHead>
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map(m => (
                        <TableHead key={m} className="text-xs font-semibold text-right px-2">{m}</TableHead>
                      ))}
                      <TableHead className="text-xs font-semibold text-right px-2 border-l bg-gray-100">Year</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(returnsData?.monthly_matrix || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={14} className="text-center text-sm text-gray-500 py-6">
                          No monthly returns yet — upload the Running-RP sheet and the transactions CSV to populate.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (returnsData?.monthly_matrix || []).map((row) => {
                        const cells = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
                        return (
                          <TableRow key={row.year} data-testid={`month-matrix-row-${row.year}`}>
                            <TableCell className="font-medium sticky left-0 bg-white">{row.year}</TableCell>
                            {cells.map(k => {
                              const v = row[k];
                              return (
                                <TableCell key={k} className={`text-right text-xs px-2 ${
                                  v === null || v === undefined ? 'text-gray-300' :
                                  v >= 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {v === null || v === undefined ? '—' : `${v.toFixed(2)}%`}
                                </TableCell>
                              );
                            })}
                            <TableCell className={`text-right font-semibold px-2 border-l bg-gray-50 ${
                              row.year_total === null || row.year_total === undefined ? 'text-gray-400' :
                              row.year_total >= 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {row.year_total === null || row.year_total === undefined ? '—' : `${row.year_total.toFixed(2)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Monthly Returns Chart with Benchmark */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Monthly Returns vs Benchmark ({formatPercent(returnsData?.summary?.benchmark_rate)} annual → {(((returnsData?.summary?.benchmark_rate || 0) / 12) || 0).toFixed(2)}% per month)
              </h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={returnsData?.monthly_returns || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(value) => `${value}%`} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Legend />
                    <Bar dataKey="return_percentage" name="Monthly Return" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    <ReferenceLine
                      y={((returnsData?.summary?.benchmark_rate || 0) / 12)}
                      stroke={COLORS.warning}
                      strokeDasharray="4 4"
                      label={{ value: 'Benchmark', fill: COLORS.warning, fontSize: 11, position: 'insideTopRight' }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Risk Summary */}
      <Card data-testid="risk-section">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1D2D2A]" />
              Risk Summary
            </CardTitle>
            <CardDescription>PAR ratios, concentration analysis, and default trends</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('risk')}
            disabled={exporting.risk}
            data-testid="export-risk-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* PAR Ratios */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Portfolio at Risk (PAR)</h4>
              <div className="space-y-4">
                {[
                  { label: 'PAR 30+ Days', value: riskData?.par_ratios?.par30, amount: riskData?.par_ratios?.par30_amount, color: 'yellow' },
                  { label: 'PAR 60+ Days', value: riskData?.par_ratios?.par60, amount: riskData?.par_ratios?.par60_amount, color: 'orange' },
                  { label: 'PAR 90+ Days', value: riskData?.par_ratios?.par90, amount: riskData?.par_ratios?.par90_amount, color: 'red' }
                ].map((par) => (
                  <div key={par.label} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{par.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{formatCurrency(par.amount || 0)}</span>
                        <Badge variant={par.value > 5 ? 'destructive' : par.value > 2 ? 'warning' : 'secondary'}>
                          {formatPercent(par.value)}
                        </Badge>
                      </div>
                    </div>
                    <Progress 
                      value={Math.min(par.value || 0, 100)} 
                      className={`h-2 ${
                        par.color === 'red' ? '[&>div]:bg-red-500' : 
                        par.color === 'orange' ? '[&>div]:bg-orange-500' : 
                        '[&>div]:bg-yellow-500'
                      }`}
                    />
                  </div>
                ))}
              </div>

              {/* PAR Trend Sparklines */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">PAR Trend (6 Months)</h4>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={riskData?.par_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(value) => `${value}%`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Area type="monotone" dataKey="par30" stackId="1" stroke="#EAB308" fill="#FEF08A" name="PAR30" />
                      <Area type="monotone" dataKey="par60" stackId="2" stroke="#F97316" fill="#FED7AA" name="PAR60" />
                      <Area type="monotone" dataKey="par90" stackId="3" stroke="#EF4444" fill="#FECACA" name="PAR90" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Concentration & Other Metrics */}
            <div className="space-y-6">
              {/* Concentration Analysis */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Concentration Analysis</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-600">Largest Single Exposure</p>
                      <p className="text-xs text-gray-400">{riskData?.concentration?.largest_single_exposure?.loan_reference}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(riskData?.concentration?.largest_single_exposure?.amount || 0)}</p>
                      <Badge variant={
                        (riskData?.concentration?.largest_single_exposure?.percentage || 0) > 20 ? 'destructive' : 'secondary'
                      }>
                        {formatPercent(riskData?.concentration?.largest_single_exposure?.percentage)}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Top 5 Loans Exposure</p>
                    <Badge variant={
                      (riskData?.concentration?.top5_exposure_percentage || 0) > 50 ? 'warning' : 'secondary'
                    }>
                      {formatPercent(riskData?.concentration?.top5_exposure_percentage)}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">Largest Sector Exposure</p>
                    <div className="text-right">
                      <p className="text-sm font-medium">{riskData?.concentration?.largest_sector?.name}</p>
                      <Badge variant="secondary">
                        {formatPercent(riskData?.concentration?.largest_sector?.percentage)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Other Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-[#1D2D2A]/5 rounded-lg border border-[#1D2D2A]/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-[#1D2D2A]" />
                    <span className="text-sm text-gray-600">Weighted Avg Maturity</span>
                  </div>
                  <p className="text-xl font-bold text-[#1D2D2A]">
                    {riskData?.weighted_average_maturity_days || 0} days
                  </p>
                </div>
                <div className={`p-4 rounded-lg border ${
                  (riskData?.default_rate?.current || 0) > 5 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className={`h-4 w-4 ${
                      (riskData?.default_rate?.current || 0) > 5 ? 'text-red-500' : 'text-gray-500'
                    }`} />
                    <span className="text-sm text-gray-600">Current Default Rate</span>
                  </div>
                  <p className={`text-xl font-bold ${
                    (riskData?.default_rate?.current || 0) > 5 ? 'text-red-600' : 'text-gray-700'
                  }`}>
                    {formatPercent(riskData?.default_rate?.current)}
                  </p>
                </div>
              </div>

              {/* Default Rate History */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Default Rate History</h4>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={riskData?.default_rate?.history || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(value) => `${value}%`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Line 
                        type="monotone" 
                        dataKey="rate" 
                        stroke={COLORS.danger}
                        strokeWidth={2}
                        dot={false}
                        name="Default Rate"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
